import type { NodeKey } from 'lexical'
import { noop } from 'lodash'
import type { PropsWithChildren } from 'react'
import { createContext, useContext, useMemo } from 'react'
import type { Integration } from '../../../generated/gql/graphql'
import type { confluenceDataProps } from '../../plugins/apps/ConfluenceAppPlugin/components/ConfluenceViewer/ConfluenceViewer'
import type { EditorCurrentUser, EditorUser } from '../../types/editor-user'

type ConnectedIntegration = { id: Integration }
type EditorMetaDataContextValue = {
    users: EditorUser[]
    currentUser: EditorCurrentUser | null
    meetingId: string | null
    meetingTitle: string | null
    isOpenContentItemsInNewTab: boolean
    timezone: string
    isPrintView: boolean
    hideAi: boolean
    hideConflunce: boolean
    hideMeetingMinutes: boolean
    hideAiCapture: boolean
    hasEditPermission: boolean
    itemId: string | null
    embeddedApp: string | null
    meetingPermissions: string[]
    connectedIntegrations: ConnectedIntegration[]
    startPresentationMode: (file: {
        id: string
        filename: string
        attachmentPath: string
    }) => void
    onPromotionalModalOpen: () => void
    handleConfluencePageEmbed: (
        nodeKey: NodeKey,
        callback: (
            nodeKey: NodeKey,
            confluencePageData: confluenceDataProps,
        ) => void,
    ) => void
    routerPush: (url: string) => void
    onOpenItemDetails: (
        itemId: string,
        itemType: string,
        sidePanelTab?: 'Tags' | 'Comments' | 'Attachment' | 'Related' | 'Votes',
    ) => void
    flags: {
        isJiraEnabled: boolean
        aiFlag: boolean
        aiPromptsFlag: boolean
        isDocumentAnnotationEnabled: boolean
        isMomahDemoEnabled: boolean
    }
}

const EditorMetaDataContext = createContext<EditorMetaDataContextValue>({
    users: [],
    currentUser: null,
    meetingId: null,
    meetingTitle: null,
    isOpenContentItemsInNewTab: false,
    timezone: 'Africa/Cairo',
    isPrintView: false,
    hideAi: false,
    hideConflunce: false,
    hideMeetingMinutes: false,
    hideAiCapture: false,
    hasEditPermission: false,
    itemId: null,
    embeddedApp: null,
    meetingPermissions: [],
    connectedIntegrations: [],
    startPresentationMode: () => {},
    onPromotionalModalOpen: noop,
    handleConfluencePageEmbed: noop,
    routerPush: noop,
    onOpenItemDetails: noop,
    flags: {
        isJiraEnabled: false,
        aiFlag: false,
        aiPromptsFlag: false,
        isDocumentAnnotationEnabled: false,
        isMomahDemoEnabled: false,
    },
})

const useEditorMetaData = () => useContext(EditorMetaDataContext)

type EditorMetaDataProviderProps = {
    users: Array<EditorUser>
    currentUser?: EditorCurrentUser | null
    meetingId?: string | null
    meetingTitle?: string | null
    openContentItemsInNewTab?: boolean
    timezone: string
    isPrintView?: boolean
    hideAi?: boolean
    hideConflunce?: boolean
    hideMeetingMinutes?: boolean
    hideAiCapture?: boolean
    hasEditPermission?: boolean
    itemId?: string | null
    embeddedApp?: string | null
    meetingPermissions?: string[]
    connectedIntegrations?: ConnectedIntegration[]
    startPresentationMode?: (file: {
        id: string
        filename: string
        attachmentPath: string
    }) => void
    onPromotionalModalOpen?: () => void
    handleConfluencePageEmbed?: (
        nodeKey: NodeKey,
        callback: (
            nodeKey: NodeKey,
            confluencePageData: confluenceDataProps,
        ) => void,
    ) => void
    routerPush?: (url: string) => void
    onOpenItemDetails?: (
        itemId: string,
        itemType: string,
        sidePanelTab?: 'Tags' | 'Comments' | 'Attachment' | 'Related' | 'Votes',
    ) => void
    flags?: {
        isJiraEnabled: boolean
        aiFlag: boolean
        aiPromptsFlag: boolean
        isDocumentAnnotationEnabled: boolean
        isMomahDemoEnabled: boolean
    }
}

const EditorMetaDataProvider = ({
    children,
    users,
    currentUser = null,
    meetingId = null,
    meetingTitle = null,
    openContentItemsInNewTab = false,
    isPrintView = false,
    hideAi = false,
    hideConflunce = false,
    hideMeetingMinutes = false,
    hideAiCapture = false,
    hasEditPermission = false,
    timezone,
    itemId = null,
    embeddedApp = null,
    meetingPermissions = [],
    connectedIntegrations = [],
    startPresentationMode = (_file: {
        id: string
        filename: string
        attachmentPath: string
    }) => {},
    onPromotionalModalOpen = noop,
    handleConfluencePageEmbed = noop,
    routerPush = noop,
    onOpenItemDetails = noop,
    flags = {
        isJiraEnabled: false,
        aiFlag: false,
        aiPromptsFlag: false,
        isDocumentAnnotationEnabled: false,
        isMomahDemoEnabled: false,
    },
}: PropsWithChildren<EditorMetaDataProviderProps>) => {
    const value: EditorMetaDataContextValue = useMemo(
        () => ({
            users,
            currentUser,
            meetingId,
            meetingTitle,
            isOpenContentItemsInNewTab: openContentItemsInNewTab,
            timezone,
            isPrintView,
            hideAi,
            hideConflunce,
            hideMeetingMinutes,
            hideAiCapture,
            hasEditPermission,
            itemId,
            embeddedApp,
            meetingPermissions,
            connectedIntegrations,
            startPresentationMode,
            onPromotionalModalOpen,
            handleConfluencePageEmbed,
            routerPush,
            onOpenItemDetails,
            flags,
        }),
        [
            users,
            currentUser,
            meetingId,
            meetingTitle,
            openContentItemsInNewTab,
            timezone,
            isPrintView,
            hideAi,
            hideConflunce,
            hideMeetingMinutes,
            hideAiCapture,
            hasEditPermission,
            itemId,
            embeddedApp,
            meetingPermissions,
            connectedIntegrations,
            startPresentationMode,
            onPromotionalModalOpen,
            handleConfluencePageEmbed,
            routerPush,
            onOpenItemDetails,
            flags,
        ],
    )

    return (
        <EditorMetaDataContext value={value}>{children}</EditorMetaDataContext>
    )
}

export { EditorMetaDataProvider, useEditorMetaData }
